import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "../../context/ToastContext";
import { useTranslation } from "react-i18next";

import FmeaApi from "../../services/FmeaApi";
import FmeaTypeEnum from "../../enum/FmeaTypeEnum";
import FmeaStatusEnum from "../../enum/FmeaStatusEnum";

// Design System
import Input from "../../components/Input/Input";
import FormCard from "../../components/FormCard/FormCard";

const FmeaTypeLabels: Record<FmeaTypeEnum, string> = {
  [FmeaTypeEnum.AIAG_2008]: "AIAG 2008",
  [FmeaTypeEnum.AIAG_VDA_2019]: "AIAG/VDA 2019",
};

const FmeaDetails: React.FC = () => {
  const hasLoaded = useRef(false);

  const { fmeaId } = useParams<{ fmeaId: string }>();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translate } = useTranslation("form");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [typeFmea, setTypeFmea] = useState<FmeaTypeEnum | null>(null);
  const [responsible, setResponsible] = useState("");
  const [status, setStatus] = useState<FmeaStatusEnum>(FmeaStatusEnum.Active);

  const [productPartNumber, setProductPartNumber] = useState("");
  const [productionUnitName, setProductionUnitName] = useState("");
  const [areaName, setAreaName] = useState("");
  const [lineName, setLineName] = useState("");
  const [sublineName, setSublineName] = useState("");

  useEffect(() => {
    if (!fmeaId || hasLoaded.current) return;
    hasLoaded.current = true;
    const load = async (): Promise<void> => {
      try {
        const fmea = await FmeaApi.GetById(fmeaId);

        setTitle(fmea.title);
        setDescription(fmea.description);
        
        // Handling potential string/number enum mismatch from API
        const typeValue = typeof fmea.fmeaType === 'string' ? FmeaTypeEnum[fmea.fmeaType as keyof typeof FmeaTypeEnum] : fmea.fmeaType;
        const statusValue = typeof fmea.status === 'string' ? FmeaStatusEnum[fmea.status as keyof typeof FmeaStatusEnum] : fmea.status;

        setTypeFmea(typeValue ?? null);
        setStatus(statusValue ?? FmeaStatusEnum.Active);

        setResponsible(fmea.responsible);

        setProductPartNumber(fmea.partNumber);
        setProductionUnitName(fmea.productionUnit);
        setAreaName(fmea.area);
        setLineName(fmea.line);
        setSublineName(fmea.subline);
      } catch (err: any) {
        console.error("Erro ao carregar detalhes da FMEA:", err);
        displayToast("error", translate("errorLoading"), err.message);
      }
    };
    load();
  }, [fmeaId, displayToast, translate]);

  const handleBack = (): void => navigate(-1);

  return (
    <FormCard
      title={translate("viewFmea")}
      icon="info"
      onSubmit={() => {}}
      onCancel={handleBack}
      cancelButtonLabel={translate("back") || "Voltar"}
      numColumns={12}
      disabled
    >
      <div style={{ gridColumn: "span 12" }}>
        <Input
          type="text"
          label={translate("title")}
          value={title || "—"}
          disabled
          onChange={() => {}}
        />
      </div>

      <div style={{ gridColumn: "span 12" }}>
        <Input
          type="textArea"
          label={translate("description")}
          value={description || "—"}
          rows={3}
          disabled
          onChange={() => {}}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translate("fmeaType")}
          value={typeFmea !== null ? FmeaTypeLabels[typeFmea] || FmeaTypeEnum[typeFmea] : "—"}
          disabled
          onChange={() => {}}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translate("responsible")}
          value={responsible || "—"}
          disabled
          onChange={() => {}}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translate("productionUnit")}
          value={productionUnitName || "—"}
          disabled
          onChange={() => {}}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translate("area")}
          value={areaName || "—"}
          disabled
          onChange={() => {}}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translate("line")}
          value={lineName || "—"}
          disabled
          onChange={() => {}}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translate("subline")}
          value={sublineName || "—"}
          disabled
          onChange={() => {}}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translate("product")}
          value={productPartNumber || "—"}
          disabled
          onChange={() => {}}
        />
      </div>

      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translate("status")}
          value={status !== undefined ? translate(FmeaStatusEnum[status]?.toLowerCase() || 'active') : "—"}
          disabled
          onChange={() => {}}
        />
      </div>
    </FormCard>
  );
};

export default FmeaDetails;
